import { SpeechRecognition } from '@capacitor-community/speech-recognition'
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core'

import { AppSettings } from './app-settings'
import { isAndroid, isIOS, isNative } from './platform'

/**
 * Thin promise-based wrapper around the platform's speech-to-text engine.
 *
 * Native (iOS / Android): `@capacitor-community/speech-recognition`.
 * Web (Chromium + Safari): the Web Speech API
 * (`window.webkitSpeechRecognition` / `window.SpeechRecognition`). This is
 * the therapist app's production path — including Safari on an iPad, where
 * WebKit's recognizer has a known failure mode after media playback (see
 * `WEB_SAFETY_TIMEOUT_MS` and `lib/audio.ts`). Production patients only
 * ever hit the Capacitor path.
 *
 * Single-shot recognition only: start, capture one utterance, stop. We
 * don't expose partial-results streaming — the drill UI takes one verdict
 * per mic tap so the simpler API is the better fit.
 *
 * One exception, web-only: {@link startWebRecognition} hands back a session
 * that listens in the BACKGROUND and is stopped explicitly. It exists for the
 * therapist comparison mode (`VITE_THERAPIST_SPEECH_ENGINE=both`), where the
 * phoneme recorder owns the mic button and this listens to the same utterance
 * so the two engines can be shown side by side. Both paths share one
 * implementation (`createWebSession`) so they can't drift.
 *
 * The match comparison is intentionally lenient (see {@link tokensMatch}):
 * lowercased + diacritics-stripped + punctuation-stripped, then a whole-
 * word containment check. So "Brot" passes when the patient says "Das
 * Brot.", and a confused "Brote" or "rot" does not. Tune this in one
 * place if production data shows we're too lenient/strict.
 *
 * iOS permissions live in `ios/App/App/Info.plist`:
 *   - NSSpeechRecognitionUsageDescription
 *   - NSMicrophoneUsageDescription
 * Android permission: `android.permission.RECORD_AUDIO` in
 * `AndroidManifest.xml`. The first call to `recognizeOnce` will trigger
 * the system permission prompt; subsequent calls reuse the grant.
 */

export interface RecognitionResult {
  /** Raw best-guess transcript. Empty string if recognition produced none. */
  transcript: string
  /** True iff the target word appears as a whole-word match (see
   *  {@link tokensMatch}). */
  matched: boolean
  /** Distinguishes "the platform decided you said the wrong thing" from
   *  "we couldn't even ask the platform". Today's UI uses this to skip
   *  auto-advancing to a wrong-verdict on plugin failure / denied
   *  permission — so a stale build (no `cap sync ios`) doesn't look like
   *  the patient was wrong on every word.
   *
   *  Values:
   *    - 'ok'                  recognition completed; matched is meaningful
   *    - 'no-permission'       mic / speech permission not granted
   *    - 'no-microphone'       no audio-capture device available (web only:
   *                            iPads always have a mic, so native never
   *                            emits this)
   *    - 'plugin-missing'      plugin isn't installed in the native app
   *                            (most often: did you run `npx cap sync ios`?)
   *    - 'unavailable'         platform doesn't speak the requested language
   *                            on-device (download required); on web this
   *                            also covers "browser has no Web Speech API"
   *    - 'error'               anything else (network speech recogniser
   *                            failed, audio session busy, etc.)
   */
  status: 'ok' | 'no-permission' | 'no-microphone' | 'plugin-missing' | 'unavailable' | 'error'
}

const LANGUAGE = 'de-DE'

/**
 * The native recogniser behind {@link recognizeOnce}.
 *
 * iOS runs our own `LingoSpeechPlugin` (ios/App/App/LingoSpeechPlugin.swift):
 * the community plugin's protocol plus `captureAudio`, which tees the audio
 * the recogniser was fed into one `audioCaptured` event after the session —
 * the voice QA corpus's sample, taken from the one microphone consumer there
 * is. A second consumer (WKWebView's getUserMedia next to the plugin's
 * AVAudioEngine) silenced the recogniser on every tap; see lib/voice-qa.ts.
 * Android keeps the community plugin: its recogniser lives in another process
 * and exposes no audio.
 */
interface NativeSpeechPlugin {
  available(): Promise<{ available: boolean }>
  checkPermissions(): Promise<{ speechRecognition: string }>
  requestPermissions(): Promise<{ speechRecognition: string }>
  start(options: {
    language: string
    maxResults: number
    partialResults: boolean
    popup: boolean
    /** iOS only: tee the recogniser's input into one `audioCaptured` event. */
    captureAudio?: boolean
  }): Promise<unknown>
  stop(): Promise<void>
  addListener(
    eventName: 'partialResults' | 'listeningState' | 'audioCaptured',
    listenerFunc: (data: unknown) => void,
  ): Promise<PluginListenerHandle>
}

const nativeSpeech: NativeSpeechPlugin = isIOS
  ? registerPlugin<NativeSpeechPlugin>('LingoSpeech')
  : // The community plugin's overloaded `addListener` is not assignable to the
    // single signature above; every call made here is one it accepts.
    (SpeechRecognition as unknown as NativeSpeechPlugin)

/** Whether {@link captureNextNativeUtterance} can deliver a clip on this platform. */
export const nativeAudioTeeAvailable: boolean = isIOS

/**
 * Dev-only stage logger. `import.meta.env.DEV` is a Vite build-time boolean
 * (true under `vite dev` / `vite dev --mode capacitor`, false under
 * `vite build` / `vite build --mode capacitor`), so production bundles drop
 * the `if (DEV)` branch and inline the helper to a no-op. Errors and
 * unexpected states stay on `console.warn` so they remain visible in
 * shipped builds.
 */
const DEV = import.meta.env.DEV
function devLog(message: string, ...args: unknown[]): void {
  if (DEV) console.log(`[speech] ${message}`, ...args)
}

/**
 * Termination tuning for the partial-results-driven recognize loop.
 *
 * Items in the drill are always single words ("Brot", "Kaffee", …), so
 * we don't need Apple's end-of-speech detector — which on this
 * plugin/iOS combo never fires for shorter utterances. Instead we treat
 * the plugin's `partialResults` stream as our activity signal: while
 * `SFSpeechRecognizer` is refining the transcription, partials arrive
 * (each with the latest best guess). When no new partial has arrived
 * for `POST_SPEECH_STABILITY_MS`, the recognizer has settled — we
 * commit the latest transcript and tell the engine to stop.
 *
 *   - PRE_SPEECH_TIMEOUT_MS: how long we wait for the FIRST partial.
 *     Catches "user never spoke" (mistap, silent room).
 *   - POST_SPEECH_STABILITY_MS: silence after the last partial. Tuned
 *     against typical SFSpeechRecognizer cadence for a single German
 *     word: partials arrive every ~150-300ms while audio is changing,
 *     stop arriving as soon as the recognizer has nothing new to add.
 *     800ms is generous enough to ride out a brief mid-word stutter
 *     without falling for the bait of "any pause = done".
 *   - SAFETY_TIMEOUT_MS: backstop against the recognition pipeline
 *     stalling entirely (no partials ever arrive, e.g. unsupported
 *     locale silently). Ensures every session ends.
 */
const PRE_SPEECH_TIMEOUT_MS = 5000
const POST_SPEECH_STABILITY_MS = 800
const SAFETY_TIMEOUT_MS = 8000

// --- public API ----------------------------------------------------------

/**
 * Capture one utterance and compare it to `target`. Resolves once the
 * platform reports the final result; rejects only on permission denial
 * or hard plugin error. A user-aborted recognition (see {@link abort})
 * resolves with `{ transcript: '', matched: false }` so the caller can
 * unwind state without a try/catch.
 */
export async function recognizeOnce(target: string): Promise<RecognitionResult> {
  devLog(
    `recognizeOnce target="${target}" language=${LANGUAGE} path=${isNative ? 'native' : 'web'}`,
  )
  if (isNative) return recognizeOnceNative(target)
  return recognizeOnceWeb(target)
}

/**
 * True iff the speech-recognition PLUGIN is registered with the native
 * runtime. Used by `recognizeOnceNative` to surface a "did you forget
 * `npx cap sync ios`?" message instead of a misleading permission error.
 *
 * Subtle: we intentionally DON'T propagate the underlying plugin's own
 * `available: false` result. That payload comes from
 * `SFSpeechRecognizer()` (no args) which checks the device's DEFAULT
 * locale — useless for us because we always request a specific language
 * (`de-DE`) at `start()` time. So a German iPad with English as system
 * language would report `available: false` here even though German
 * recognition starts fine. We only count an actual thrown exception
 * (Capacitor's "plugin not implemented" bridge error) as proof the
 * plugin's missing.
 */
export async function isAvailable(): Promise<boolean> {
  if (!isNative) return getWebRecognitionCtor() !== null
  try {
    await nativeSpeech.available()
    return true
  } catch (e) {
    console.warn('[speech] available() rejected — plugin likely not in the native build:', e)
    return false
  }
}

/**
 * Ensure mic + speech-recognition permissions are granted. Triggers the
 * system prompt on first call if necessary. Returns true iff fully
 * authorised after the prompt. On web, returns true if the API exists
 * (the browser prompts inline on first `start()`).
 *
 * iOS only ever prompts the user ONCE. After the first "Don't Allow",
 * `requestPermissions()` returns 'denied' immediately without
 * re-prompting — the user has to flip the toggle in
 * Settings → LingoTalk → Speech Recognition / Microphone manually.
 * The training view exposes a shortcut to that page via
 * {@link openAppSettings}.
 */
export async function ensurePermissions(): Promise<boolean> {
  if (!isNative) return getWebRecognitionCtor() !== null

  try {
    const status = await nativeSpeech.checkPermissions()
    devLog(`checkPermissions → ${status.speechRecognition}`)
    if (status.speechRecognition === 'granted') return true

    const requested = await nativeSpeech.requestPermissions()
    devLog(`requestPermissions → ${requested.speechRecognition}`)
    return requested.speechRecognition === 'granted'
  } catch (e) {
    console.warn('[speech] permission check failed:', e)
    return false
  }
}

/**
 * Open the OS Settings app to LingoTalk's permission page so the user
 * can flip Microphone / Speech Recognition back on after a previous
 * "Don't Allow".
 *
 * iOS uses the `app-settings:` URL scheme (handled natively from the
 * Capacitor WebView via `window.location.href`). Android calls our
 * in-app `AppSettings` Capacitor plugin, which fires the
 * `Settings.ACTION_APPLICATION_DETAILS_SETTINGS` intent — see
 * `android/app/src/main/java/com/digola/lingotalk/AppSettingsPlugin.java`.
 * No-op on web.
 */
export function openAppSettings(): void {
  if (!isNative) return
  if (isIOS) {
    try {
      window.location.href = 'app-settings:'
    } catch (e) {
      console.warn('[speech] failed to open iOS settings:', e)
    }
    return
  }
  if (isAndroid) {
    void AppSettings.open().catch((e) => {
      console.warn('[speech] failed to open Android settings:', e)
    })
  }
}

/**
 * Cancel any in-flight recognition. The current `recognizeOnce` promise
 * resolves with whatever transcript was captured so far so the caller
 * doesn't have to wrap an abort path in try/catch.
 *
 * On native this routes through the active session's `finish()` —
 * tears down all listeners + timers, calls `stop()` on the plugin,
 * resolves the promise. Calling `stop()` directly without unwinding
 * the session would leave the partials listener attached for the next
 * session to inherit.
 */
export async function abort(): Promise<void> {
  if (isNative) {
    if (activeFinish !== null) {
      activeFinish()
      return
    }
    // No active session — call stop() defensively in case the engine
    // is running from some other code path that crashed.
    try {
      await nativeSpeech.stop()
    } catch {
      // Already stopped — harmless.
    }
    return
  }
  webActive?.abort()
  webActive = null
}

// --- native (Capacitor plugin) ------------------------------------------

export interface NativeTeeClip {
  /** 16 kHz mono 16-bit WAV — the recogniser's input, byte for byte. */
  blob: Blob
  sampleRate: number
  channels: number
  durationMs: number
}

/**
 * How long after a native session ends we still accept its `audioCaptured`
 * event. The plugin sends it from the same call that stops the engine, so in
 * practice it lands within a bridge round-trip of `stop()`.
 */
const NATIVE_TEE_GRACE_MS = 3000

/**
 * A tee request that no recognition picked up resolves null after this long:
 * the safety ceiling of a session plus the grace above, with margin.
 */
const NATIVE_TEE_ORPHAN_MS = SAFETY_TIMEOUT_MS + NATIVE_TEE_GRACE_MS + 5000

/** The pending {@link captureNextNativeUtterance}, consumed by the next native session. */
let teeWaiter: ((clip: NativeTeeClip | null) => void) | null = null

/**
 * Ask the next native recognition to hand back the audio it fed the
 * recogniser. Resolves once that session has ended: with the clip, or with
 * null when there is none (Android, a session that never started, a lost
 * event). Never rejects, never delays the verdict — `recognizeOnce` settles
 * on its own timers and this resolves after it. Call it immediately before
 * `recognizeOnce`; a request nothing consumed resolves null on its own.
 */
export function captureNextNativeUtterance(): Promise<NativeTeeClip | null> {
  if (!nativeAudioTeeAvailable) return Promise.resolve(null)
  teeWaiter?.(null)
  return new Promise((resolve) => {
    const waiter = (clip: NativeTeeClip | null): void => {
      if (teeWaiter === waiter) teeWaiter = null
      resolve(clip)
    }
    teeWaiter = waiter
    setTimeout(() => waiter(null), NATIVE_TEE_ORPHAN_MS)
  })
}

/** The plugin's `audioCaptured` payload as a clip, or null if it is not one. */
function decodeTeeEvent(data: unknown): NativeTeeClip | null {
  const d = data as {
    audio?: unknown
    sampleRate?: unknown
    channels?: unknown
    durationMs?: unknown
  }
  if (typeof d.audio !== 'string' || d.audio.length === 0) return null
  let bytes: Uint8Array
  try {
    const binary = atob(d.audio)
    bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  } catch {
    return null
  }
  return {
    blob: new Blob([bytes], { type: 'audio/wav' }),
    sampleRate: typeof d.sampleRate === 'number' ? d.sampleRate : 16000,
    channels: typeof d.channels === 'number' ? d.channels : 1,
    durationMs: typeof d.durationMs === 'number' ? d.durationMs : 0,
  }
}

/**
 * Reference to the in-flight session's terminator. Lets `abort()` unwind
 * a session from outside the recognize call (pause / close), without
 * needing to bare-call `stop()` and leak the partials listener.
 */
let activeFinish: (() => void) | null = null

async function recognizeOnceNative(target: string): Promise<RecognitionResult> {
  if (!(await isAvailable())) {
    console.warn(
      '[speech] plugin not available — likely not registered with the native project. Run `npx cap sync ios` and rebuild.',
    )
    return { transcript: '', matched: false, status: 'plugin-missing' }
  }

  const ok = await ensurePermissions()
  if (!ok) {
    return { transcript: '', matched: false, status: 'no-permission' }
  }

  // The drill always asks for a single word. SFSpeechRecognizer's own
  // end-of-speech detection drags on for several seconds even after the
  // user clearly finished a one-syllable answer, so we drive termination
  // from the partial-results stream instead:
  //
  //   - Each `partialResults` event carries the recognizer's current
  //     best-guess transcription. While audio is changing the recognizer
  //     refines, emitting new partials every ~150-300ms.
  //   - When the latest partial stops being updated for
  //     POST_SPEECH_STABILITY_MS, the recognizer has settled — we commit
  //     the latest transcript as the answer.
  //
  // Two backstops:
  //   - PRE_SPEECH_TIMEOUT_MS: nothing arrived → user never spoke.
  //   - SAFETY_TIMEOUT_MS: absolute ceiling against any path where
  //     partials arrive forever (or never, and the pre-speech timer
  //     somehow failed to fire).
  //
  // Crucially we do NOT depend on `SpeechRecognition.start()` resolving:
  // we've observed it hanging in `partialResults: true` mode on this
  // iOS/plugin combination. Whether or not start's promise settles, the
  // listener still receives events from the native side, so the session
  // can complete via the stability timer alone.
  let latestTranscript = ''
  let preSpeechTimer: ReturnType<typeof setTimeout> | null = null
  let stabilityTimer: ReturnType<typeof setTimeout> | null = null
  let safetyTimer: ReturnType<typeof setTimeout> | null = null
  let partialsHandle: PluginListenerHandle | null = null
  let stateHandle: PluginListenerHandle | null = null
  let finished = false

  return new Promise<RecognitionResult>((resolve) => {
    // Voice QA may have asked for this utterance's audio. The plugin tees it
    // and sends one `audioCaptured` as the engine stops — i.e. from our own
    // stop() in finish(), or from the recogniser's final result — so this
    // listener outlives the session's others by a grace period.
    const tee = teeWaiter
    teeWaiter = null
    let teeHandle: PluginListenerHandle | null = null
    let teeSettled = false
    function settleTee(clip: NativeTeeClip | null): void {
      if (teeSettled) return
      teeSettled = true
      void teeHandle?.remove()
      teeHandle = null
      tee?.(clip)
    }
    if (tee !== null) {
      nativeSpeech
        .addListener('audioCaptured', (data) => settleTee(decodeTeeEvent(data)))
        .then((handle) => {
          if (teeSettled) {
            void handle.remove()
            return
          }
          teeHandle = handle
        })
    }

    function finish(status: RecognitionResult['status'], reason: string): void {
      if (finished) return
      finished = true
      const matched = status === 'ok' && tokensMatch(latestTranscript, target)
      devLog(
        `finish(reason=${reason}) status=${status} transcript="${latestTranscript}" matched=${matched}`,
      )
      if (DEV && status === 'ok' && latestTranscript.length > 0) {
        // Surface the normalised token comparison so we can see WHY a
        // transcript did or didn't match (e.g. "Brote" vs "Brot" looks
        // similar but normalises to a different token).
        devLog(
          `match heard=[${normaliseForMatch(latestTranscript).join(',')}] target=[${normaliseForMatch(target).join(',')}]`,
        )
      }
      if (preSpeechTimer !== null) clearTimeout(preSpeechTimer)
      if (stabilityTimer !== null) clearTimeout(stabilityTimer)
      if (safetyTimer !== null) clearTimeout(safetyTimer)
      // Remove listeners BEFORE stopping so a late partial-results event
      // can't sneak past and arm a new stability timer.
      void partialsHandle?.remove()
      void stateHandle?.remove()
      partialsHandle = null
      stateHandle = null
      // Tell the plugin to tear the audio engine down. Calls `endAudio()`
      // on the recognition request → recognizer commits → engine stops
      // → mic indicator drops. Idempotent if it already stopped.
      void nativeSpeech.stop().catch((e) => {
        devLog(`stop() rejected (likely already stopped): ${String(e)}`)
      })
      if (tee !== null) setTimeout(() => settleTee(null), NATIVE_TEE_GRACE_MS)
      activeFinish = null
      resolve({ transcript: latestTranscript, matched, status })
    }

    // 1. Safety net armed synchronously, before any await, so a hung
    //    `start()` or listener-attach can't keep the session open
    //    indefinitely.
    safetyTimer = setTimeout(() => finish('ok', 'safety'), SAFETY_TIMEOUT_MS)

    // 2. Pause / close from the training view routes through here so the
    //    listener and engine unwind cleanly.
    activeFinish = () => finish('ok', 'abort')

    // 3. Subscribe to partials BEFORE calling start(). The plugin's
    //    `notifyListeners('partialResults', …)` only reaches us once
    //    JS-side `addListener` has registered with the bridge.
    nativeSpeech
      .addListener('partialResults', (data: unknown) => {
        if (finished) return
        const matches = (data as { matches?: unknown }).matches
        if (!Array.isArray(matches)) return
        const transcript = typeof matches[0] === 'string' ? matches[0] : ''
        devLog(`partial="${transcript}" (alternatives=${matches.length})`)
        if (transcript.length === 0) return

        latestTranscript = transcript

        // The user has spoken — cancel the "never spoke" guard.
        if (preSpeechTimer !== null) {
          clearTimeout(preSpeechTimer)
          preSpeechTimer = null
          devLog('pre-speech timer cleared (first partial arrived)')
        }
        // Reset the stability window. The recognizer has new info to share;
        // we'll commit when it goes quiet for POST_SPEECH_STABILITY_MS.
        if (stabilityTimer !== null) clearTimeout(stabilityTimer)
        stabilityTimer = setTimeout(() => finish('ok', 'stability'), POST_SPEECH_STABILITY_MS)
      })
      .then((handle) => {
        if (finished) {
          void handle.remove()
          return
        }
        partialsHandle = handle
        devLog('partialResults listener attached')
      })

    // 4. listeningState lets us see audio-engine lifecycle in the log —
    //    'started' should fire as soon as start() boots the engine,
    //    'stopped' after our stop() call (or the recognizer's own
    //    isFinal). Useful as a sanity check that the native side is
    //    actually running.
    nativeSpeech
      .addListener('listeningState', (data: unknown) => {
        devLog(`listeningState=${JSON.stringify(data)}`)
      })
      .then((handle) => {
        if (finished) {
          void handle.remove()
          return
        }
        stateHandle = handle
        devLog('listeningState listener attached')
      })

    // 5. Kick off recognition. We DON'T await the promise — partial
    //    events are the primary signal. The `.then`/`.catch` is purely
    //    for logging the start() outcome (and short-circuiting on the
    //    plugin-missing case, which would normally throw immediately).
    devLog(`calling start(language=${LANGUAGE}, partialResults=true, captureAudio=${tee !== null})`)
    nativeSpeech
      .start({
        language: LANGUAGE,
        maxResults: 1,
        partialResults: true,
        popup: false,
        captureAudio: tee !== null,
      })
      .then(() => {
        if (finished) return
        devLog(`start() resolved, arming pre-speech timer (${PRE_SPEECH_TIMEOUT_MS}ms)`)
        preSpeechTimer = setTimeout(() => finish('ok', 'pre-speech'), PRE_SPEECH_TIMEOUT_MS)
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : String(e)
        const status: RecognitionResult['status'] =
          /not implemented|UNIMPLEMENTED|no such plugin|not available/i.test(message)
            ? 'plugin-missing'
            : 'error'
        console.warn(`[speech] start() rejected (${status}):`, message)
        finish(status, 'start-rejected')
      })
  })
}

// --- web (dev fallback + the therapist comparison mode) ------------------

interface WebRecognitionErrorEvent {
  error: string
}

/**
 * Structural typing for the Web Speech API's result tree, deliberately loose:
 * every field is optional and the collections are index signatures rather than
 * the real `SpeechRecognitionResultList`. We read it defensively because
 * engines disagree on whether `confidence` is populated at all, and interim
 * results carry `isFinal: false` with a transcript that may still change.
 */
interface WebAlternative {
  transcript?: string
  confidence?: number
}
interface WebResult {
  readonly length?: number
  readonly isFinal?: boolean
  [index: number]: WebAlternative
}
interface WebResultList {
  readonly length?: number
  [index: number]: WebResult
}
interface WebResultEvent {
  results: WebResultList
}

interface WebRecognitionInstance {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  start(): void
  /** Graceful end — commits the final result, unlike `abort()` which drops it. */
  stop(): void
  abort(): void
  onresult: ((event: WebResultEvent) => void) | null
  onerror: ((event: WebRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
}

interface WebRecognitionCtor {
  new (): WebRecognitionInstance
}

function getWebRecognitionCtor(): WebRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: WebRecognitionCtor
    webkitSpeechRecognition?: WebRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/**
 * Map the Web Speech API's `SpeechRecognitionErrorEvent.error` strings to
 * our internal status enum. Spec values per
 * https://wicg.github.io/speech-api/#dom-speechrecognitionerrorcode:
 *
 *   not-allowed / service-not-allowed → permission denied
 *   audio-capture                     → no microphone hardware
 *   language-not-supported            → German pack unavailable
 *   no-speech                         → user was silent (not an error)
 *   aborted                           → caller invoked .abort()
 *   network                           → cloud recogniser unreachable
 *
 * Anything else collapses to 'error'.
 */
function webErrorToStatus(error: string): RecognitionResult['status'] | 'ok-silent' {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'no-permission'
    case 'audio-capture':
      return 'no-microphone'
    case 'language-not-supported':
      return 'unavailable'
    case 'no-speech':
    case 'aborted':
      // Not an error from the UI's perspective — treat as a silent
      // session, equivalent to the native pre-speech timeout: caller
      // gets status 'ok' with matched=false, no banner.
      return 'ok-silent'
    case 'network':
    default:
      return 'error'
  }
}

/**
 * A browser recognition result plus the extra fields the engine-comparison UI
 * shows. `recognizeOnce`'s {@link RecognitionResult} contract is unchanged —
 * this superset is returned only by {@link startWebRecognition}.
 */
export interface WebRecognitionDetail extends RecognitionResult {
  /** Confidence of the winning alternative (0–1). `null` when not reported. */
  confidence: number | null
  /** Best-first alternatives including the winner; may be empty. */
  alternatives: string[]
}

/**
 * A recognition running in the BACKGROUND, next to something else that owns the
 * user-facing mic button — today only `TrainingView`'s dual-engine comparison
 * mode, where `lib/audio-recorder.ts` records the clip for our phoneme scorer
 * while this listens to the same utterance.
 *
 * Web-only. The Web Speech API cannot be handed a recorded blob, so running
 * "the same audio" through both engines means capturing once with both of them
 * live; there is no native equivalent and none is needed (dual mode is gated on
 * `isWeb` by `lib/speech-engine.ts`).
 */
export interface WebRecognitionSession {
  /**
   * Stop listening and resolve with what the recognizer settled on. Safe to
   * call more than once — later calls return the same promise. Never rejects:
   * a permission/hardware/engine failure comes back as a non-'ok' `status` so
   * a broken comparison engine can never break the drill.
   */
  finish(): Promise<WebRecognitionDetail>
  /** Discard the session without a usable result (pause / close / unmount). */
  abort(): void
}

/**
 * How long {@link WebRecognitionSession.finish} waits for `onend` after asking
 * the recognizer to stop, before settling with whatever arrived. Chrome
 * normally ends within a few hundred ms; this backstop is what guarantees a
 * stalled comparison engine can't hold the drill up.
 */
const WEB_FINISH_TIMEOUT_MS = 1500

/**
 * Backstop for {@link recognizeOnceWeb}, the web twin of the native
 * `SAFETY_TIMEOUT_MS`. The one-shot path relies on the recognizer's own
 * endpointing to end the session, and on iPadOS Safari that endpointing can
 * silently never happen: once an `<audio>`/`<video>` element has played in the
 * session, WebKit's recognizer starts, lights the mic indicator, and then
 * delivers neither `onresult` nor `onerror` nor `onend`
 * (https://bugs.webkit.org/show_bug.cgi?id=321436). Without this timer the
 * drill's mic button stays red for ever. When it fires we ask the recognizer to
 * stop — which commits a final result if it has one — and then settle with
 * whatever arrived, exactly as the dual-engine `finish()` does.
 *
 * Longer than the native 8 s on purpose: a therapist in a browser may read the
 * word off the screen before speaking, and a false cut counts as a miss.
 */
const WEB_SAFETY_TIMEOUT_MS = 10000

let webActive: WebRecognitionInstance | null = null

interface InternalWebSession {
  readonly promise: Promise<WebRecognitionDetail>
  stop(): void
  abort(): void
  /** Resolve now with whatever we have — the `finish()` timeout backstop. */
  settleNow(): void
}

/**
 * The single Web Speech API implementation. {@link recognizeOnceWeb} awaits the
 * recognizer's own endpointing; the dual-engine session stops it explicitly
 * when the phoneme recorder stops. Both go through here so the two paths can't
 * drift apart.
 */
function createWebSession(
  target: string,
  opts: { interimResults: boolean; maxAlternatives: number },
): InternalWebSession | null {
  const Ctor = getWebRecognitionCtor()
  if (Ctor === null) return null

  const r = new Ctor()
  r.lang = LANGUAGE
  r.interimResults = opts.interimResults
  r.maxAlternatives = opts.maxAlternatives

  let transcript = ''
  let confidence: number | null = null
  let alternatives: string[] = []
  // Default to 'ok'; an error event overrides this before onend fires.
  let finalStatus: RecognitionResult['status'] = 'ok'
  let settled = false
  let resolveResult: (value: WebRecognitionDetail) => void = () => {}

  const promise = new Promise<WebRecognitionDetail>((resolve) => {
    resolveResult = resolve
  })

  function settle(reason: string): void {
    if (settled) return
    settled = true
    if (webActive === r) webActive = null
    const matched = finalStatus === 'ok' && tokensMatch(transcript, target)
    devLog(
      `web settle(${reason}) transcript="${transcript}" matched=${matched} status=${finalStatus}`,
    )
    if (DEV && finalStatus === 'ok' && transcript.length > 0) {
      devLog(
        `match heard=[${normaliseForMatch(transcript).join(',')}] target=[${normaliseForMatch(target).join(',')}]`,
      )
    }
    resolveResult({ transcript, matched, status: finalStatus, confidence, alternatives })
  }

  r.onresult = (event) => {
    // With `interimResults` on, every event carries the whole result list and
    // the newest entry is the one to read; with it off there is only ever one.
    const list = event.results
    const index = typeof list.length === 'number' ? Math.max(0, list.length - 1) : 0
    const result = list[index] ?? list[0]
    if (result === undefined) return
    const best = result[0]
    const text = best?.transcript
    // An empty interim would otherwise wipe a good earlier guess.
    if (typeof text !== 'string' || text.length === 0) return

    transcript = text
    confidence = typeof best.confidence === 'number' ? best.confidence : null
    const count = typeof result.length === 'number' ? result.length : 1
    const heard: string[] = []
    for (let i = 0; i < count; i++) {
      const alt = result[i]?.transcript
      if (typeof alt === 'string' && alt.length > 0) heard.push(alt)
    }
    alternatives = heard
    devLog(`web onresult transcript="${transcript}" final=${result.isFinal !== false}`)
  }

  r.onerror = (event) => {
    const mapped = webErrorToStatus(event.error)
    if (mapped === 'ok-silent') {
      devLog(`web onerror error=${event.error} → treated as silent`)
      return
    }
    finalStatus = mapped
    console.warn(`[speech] web recognition error: ${event.error} → status=${mapped}`)
  }

  r.onend = () => settle('onend')

  webActive = r
  try {
    devLog(`web start(lang=${LANGUAGE}, interim=${opts.interimResults})`)
    r.start()
  } catch (e) {
    // Some browsers throw if start() is called twice quickly.
    console.warn('[speech] web recognition start failed:', e)
    finalStatus = 'error'
    settle('start-threw')
  }

  return {
    promise,
    stop(): void {
      try {
        r.stop()
      } catch (e) {
        devLog(`web stop() threw — settling with what we have: ${String(e)}`)
        settle('stop-threw')
      }
    },
    abort(): void {
      try {
        r.abort()
      } catch {
        // Already stopped — harmless.
      }
      settle('abort')
    },
    settleNow(): void {
      settle('finish-timeout')
    },
  }
}

function recognizeOnceWeb(target: string): Promise<RecognitionResult> {
  const session = createWebSession(target, { interimResults: false, maxAlternatives: 1 })
  if (session === null) {
    return Promise.resolve({ transcript: '', matched: false, status: 'unavailable' })
  }
  // Normally the recognizer's own endpointing ends the session, which is what
  // makes this the "one utterance per mic tap" API. The safety timer only
  // matters when that never comes (see WEB_SAFETY_TIMEOUT_MS).
  let finishTimer: ReturnType<typeof setTimeout> | null = null
  const safetyTimer = setTimeout(() => {
    console.warn(
      `[speech] web recognition produced no end event within ${WEB_SAFETY_TIMEOUT_MS}ms — stopping it`,
    )
    session.stop()
    finishTimer = setTimeout(() => session.settleNow(), WEB_FINISH_TIMEOUT_MS)
  }, WEB_SAFETY_TIMEOUT_MS)
  void session.promise.finally(() => {
    clearTimeout(safetyTimer)
    if (finishTimer !== null) clearTimeout(finishTimer)
  })
  return session.promise
}

/**
 * Start a background browser recognition for the dual-engine comparison mode.
 * Returns `null` when the browser has no Web Speech API at all, which callers
 * should treat as "no comparison available", not as a failure of the run.
 *
 * Interim results are on (the phoneme recorder's voice-activity detection may
 * stop us before Chrome commits a final, and the newest interim is a much
 * better answer than an empty one) and up to three alternatives are requested
 * so the comparison can show what else the engine considered.
 */
export function startWebRecognition(target: string): WebRecognitionSession | null {
  const session = createWebSession(target, { interimResults: true, maxAlternatives: 3 })
  if (session === null) return null

  let stopping = false
  return {
    finish(): Promise<WebRecognitionDetail> {
      if (!stopping) {
        stopping = true
        session.stop()
        const timer = setTimeout(() => session.settleNow(), WEB_FINISH_TIMEOUT_MS)
        void session.promise.finally(() => clearTimeout(timer))
      }
      return session.promise
    },
    abort(): void {
      session.abort()
    },
  }
}

/**
 * Report the browser's current microphone-permission state without
 * triggering a prompt. Used by the in-app help dialog so we can phrase
 * instructions differently for "you previously denied" vs "permission
 * hasn't been asked yet" vs "we can't tell".
 *
 * Coverage:
 *   - Chrome / Edge: `navigator.permissions.query({ name: 'microphone' })`
 *     returns 'granted' | 'denied' | 'prompt'.
 *   - Safari: Permissions API exists but historically rejects 'microphone'
 *     as an unknown name. Treated as 'unsupported'.
 *   - Firefox: Web Speech API is missing anyway, so this path is unreached.
 *
 * No-op on native (returns 'unsupported') — native uses the Capacitor
 * permission status directly via {@link ensurePermissions}.
 */
export async function webMicPermissionState(): Promise<
  'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown'
> {
  if (isNative) return 'unsupported'
  const permissions = (navigator as Navigator & { permissions?: Permissions }).permissions
  if (permissions === undefined) return 'unsupported'
  try {
    const status = await permissions.query({ name: 'microphone' as PermissionName })
    return status.state as 'granted' | 'denied' | 'prompt'
  } catch {
    // Safari rejects unknown permission names with a TypeError.
    return 'unsupported'
  }
}

// --- token matching -----------------------------------------------------

/**
 * Normalise: lowercased, NFD-decomposed (so "ä" → "a" + combining diacritic),
 * combining marks stripped, then every non-letter collapsed to a single space.
 * Result is whitespace-separated token list of plain ASCII-letter words.
 */
export function normaliseForMatch(s: string): string[] {
  return s
    .toLowerCase()
    .normalize('NFD')

    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z\s]+/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 0)
}

/**
 * Whole-word containment. `target` must appear as a complete token in
 * `transcript`. "Das Brot." matches target "Brot"; "rot" does not match
 * "Brot"; "Brote" does not match "Brot" (different word boundary).
 *
 * Exported so unit tests can pin the behaviour.
 */
export function tokensMatch(transcript: string, target: string): boolean {
  const targetTokens = normaliseForMatch(target)
  if (targetTokens.length === 0) return false
  const haystack = normaliseForMatch(transcript)
  if (haystack.length === 0) return false

  // Multi-word targets (rare for items, but supported): every token of
  // the target must appear somewhere in the haystack in order, contiguous.
  if (targetTokens.length === 1) {
    return haystack.includes(targetTokens[0])
  }
  for (let i = 0; i <= haystack.length - targetTokens.length; i++) {
    let match = true
    for (let j = 0; j < targetTokens.length; j++) {
      if (haystack[i + j] !== targetTokens[j]) {
        match = false
        break
      }
    }
    if (match) return true
  }
  return false
}
